import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconPlus,
} from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useOrgs } from '../api/orgs';
import {
  useOrgProductAssignments,
  useRevokeAssignment,
  type OrgProductAssignment,
} from '../api/products';
import { useWorkflows, type WorkflowDefinition } from '../api/workflows';
import { AssignProductModal } from '../components/assign-product-modal';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

/**
 * Pick the most relevant workflow per productId for the
 * "Workflow" column. Preference:
 *   1. Org-specific row over a product-wide default (matches the
 *      resolver in orchestrator's `findActiveFor`).
 *   2. Within the same scope: `active` > `draft` > `archived`
 *      (only `active` actually unblocks case creation, but we
 *      surface drafts so operators can see in-progress work, and
 *      archived rows so they can see history).
 *   3. Within the same scope + status: newest `version` wins.
 *
 * Skips templates (`isTemplate=true`) entirely — they're
 * starters to clone from, not production rows.
 */
function pickWorkflowsByProduct(rows: WorkflowDefinition[]): Map<string, WorkflowDefinition> {
  const STATUS_RANK: Record<WorkflowDefinition['status'], number> = {
    active: 0,
    draft: 1,
    archived: 2,
  };
  // Lower rank wins. (0, 0) is the best: org-specific + active.
  function rank(w: WorkflowDefinition): [number, number] {
    return [w.orgId === null ? 1 : 0, STATUS_RANK[w.status]];
  }
  const out = new Map<string, WorkflowDefinition>();
  for (const w of rows) {
    if (!w.productId) continue;
    if (w.isTemplate) continue;
    const existing = out.get(w.productId);
    if (!existing) {
      out.set(w.productId, w);
      continue;
    }
    const [a1, a2] = rank(w);
    const [b1, b2] = rank(existing);
    if (a1 < b1 || (a1 === b1 && (a2 < b2 || (a2 === b2 && w.version > existing.version)))) {
      out.set(w.productId, w);
    }
  }
  return out;
}

function summariseTrust(a: OrgProductAssignment): string {
  const parts: string[] = [];
  if (a.actorContextJwksUrl) parts.push('JWKS');
  if (a.actorContextIssuer) parts.push('iss');
  if (a.actorContextAudience) parts.push('aud');
  return parts.length === 0 ? '—' : parts.join(' · ');
}

export function OrgProductsPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const active = useOrgProductAssignments(orgId, { includeRevoked: false });
  const revokedQuery = useOrgProductAssignments(orgId, { includeRevoked: true });
  const orgs = useOrgs();
  const orgName = (orgs.data ?? []).find((o) => o.id === orgId)?.name ?? orgId;
  // List workflows scoped to this org once; join client-side in
  // the table below. Server returns drafts + active + archived;
  // we surface the most relevant row per product (active > draft).
  const workflows = useWorkflows({ orgId });
  const workflowByProductId = pickWorkflowsByProduct(workflows.data?.items ?? []);

  const revoke = useRevokeAssignment(orgId);

  const [assignOpen, { open: openAssign, close: closeAssign }] = useDisclosure(false);
  const [revokedOpen, { toggle: toggleRevoked }] = useDisclosure(false);
  const [error, setError] = useState<string | null>(null);

  function handleRevoke(productId: string) {
    setError(null);
    revoke.mutate(productId, {
      onSuccess: () => notifications.show({ color: 'green', message: 'Assignment revoked.' }),
      onError: (err) => {
        if (err instanceof ApiError) {
          try {
            const parsed = JSON.parse(err.body) as { message?: string };
            setError(parsed.message ?? 'Could not revoke assignment.');
            return;
          } catch {
            /* fall through */
          }
        }
        setError('Could not revoke assignment.');
      },
    });
  }

  const activeAssignments = active.data ?? [];
  const allAssignments = revokedQuery.data ?? [];
  const revokedAssignments = allAssignments.filter((a) => a.revokedAt !== null);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>{orgName}</Title>
          <Anchor component={Link} to={`/orgs/${orgId}`} size="sm">
            ← back to organisation
          </Anchor>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={openAssign}>
          Assign product
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={5}>Active assignments</Title>
          {activeAssignments.length === 0 ? (
            <Text c="dimmed">No products assigned to this organisation yet.</Text>
          ) : (
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Display name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Workflow</Table.Th>
                  <Table.Th>Trust config</Table.Th>
                  <Table.Th>Assigned</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {activeAssignments.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/products/${a.productId}`} size="sm">
                        {a.productCode}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{a.productDisplayName}</Table.Td>
                    <Table.Td>
                      <Badge color={a.status === 'active' ? 'green' : 'yellow'} variant="light">
                        {a.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <WorkflowCell
                        orgId={orgId}
                        productId={a.productId}
                        workflow={workflowByProductId.get(a.productId) ?? null}
                      />
                    </Table.Td>
                    <Table.Td>{summariseTrust(a)}</Table.Td>
                    <Table.Td>{formatDate(a.assignedAt)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          variant="default"
                          size="xs"
                          component={Link}
                          to={`/orgs/${orgId}/products/${a.productId}/integration`}
                        >
                          Integrate
                        </Button>
                        <Button
                          variant="default"
                          size="xs"
                          component={Link}
                          to={`/orgs/${orgId}/products/${a.productId}/consent`}
                        >
                          Consents
                        </Button>
                        <Button
                          variant="subtle"
                          color="red"
                          size="xs"
                          onClick={() => handleRevoke(a.productId)}
                          disabled={revoke.isPending}
                        >
                          Revoke
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={5}>Revoked assignments ({revokedAssignments.length})</Title>
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={toggleRevoked}
              leftSection={
                revokedOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />
              }
            >
              {revokedOpen ? 'Hide' : 'Show'}
            </Button>
          </Group>
          <Collapse in={revokedOpen}>
            {revokedAssignments.length === 0 ? (
              <Text c="dimmed">No history.</Text>
            ) : (
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Code</Table.Th>
                    <Table.Th>Assigned</Table.Th>
                    <Table.Th>Revoked</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {revokedAssignments.map((a) => (
                    <Table.Tr key={a.id}>
                      <Table.Td>{a.productCode}</Table.Td>
                      <Table.Td>{formatDate(a.assignedAt)}</Table.Td>
                      <Table.Td>{formatDate(a.revokedAt)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Collapse>
        </Stack>
      </Card>

      <AssignProductModal
        opened={assignOpen}
        onClose={closeAssign}
        orgId={orgId}
        alreadyAssignedProductIds={activeAssignments.map((a) => a.productId)}
      />
    </Stack>
  );
}

/**
 * Renders the workflow status for one (org, product) row. Three
 * states:
 *   - active workflow exists → name + edit link, "active" badge.
 *   - draft only → name + edit link, "draft" badge, hint that
 *     cases will be refused until published.
 *   - none → "Create workflow" CTA with org_id + product_id
 *     prefilled in the deep-link, so the operator lands on the
 *     new-workflow page with the slot already chosen.
 */
function WorkflowCell({
  orgId,
  productId,
  workflow,
}: {
  orgId: string;
  productId: string;
  workflow: WorkflowDefinition | null;
}) {
  if (!workflow) {
    return (
      <Anchor
        component={Link}
        to={`/workflows/new?org_id=${encodeURIComponent(orgId)}&product_id=${encodeURIComponent(productId)}`}
        size="sm"
      >
        Create workflow
      </Anchor>
    );
  }
  const isActive = workflow.status === 'active';
  const isDefault = workflow.orgId === null;
  return (
    <Group gap="xs" wrap="nowrap">
      <Anchor component={Link} to={`/workflows/${workflow.id}`} size="sm">
        {workflow.name}
      </Anchor>
      <Badge
        size="sm"
        variant="light"
        color={isActive ? 'green' : workflow.status === 'draft' ? 'yellow' : 'gray'}
      >
        {workflow.status}
      </Badge>
      {isDefault && (
        <Badge
          size="sm"
          variant="outline"
          color="blue"
          title="Product-wide default — applies until this org publishes its own override."
        >
          default
        </Badge>
      )}
    </Group>
  );
}
